/**
 * Goals Page - Second onboarding page (Step 2 of 4).
 * Feature 016: Conversational onboarding flow for goal setting.
 *
 * User Story 3: "What do you want most right now? 🎯"
 * Collects: primary_goal (required), target_weight (optional), target_waist (optional)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOnboarding } from '../../hooks/useOnboarding';
import ProgressIndicator from '../../components/onboarding/ProgressIndicator';

// T038: Primary goal options (4 cards in 2x2 grid)
const PRIMARY_GOALS = [
  { value: 'lose_weight', label: 'Lose weight' },
  { value: 'lose_belly_fat', label: 'Lose belly fat' },
  { value: 'build_strength', label: 'Build Strength' },
  { value: 'improve_energy', label: 'Improve energy' }
];

const GoalsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { savePage, isSaving } = useOnboarding();

  // Form state
  const [primaryGoal, setPrimaryGoal] = useState('');
  const [targetWeight, setTargetWeight] = useState('');
  const [targetWaist, setTargetWaist] = useState('');

  // Validation state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  // Pre-fill existing data if user has already saved goals
  useEffect(() => {
    if (user?.profile) {
      const { primary_goal, physical_profile } = user.profile;

      if (primary_goal) setPrimaryGoal(primary_goal);

      if (physical_profile) {
        if (physical_profile.target_weight) {
          setTargetWeight(physical_profile.target_weight.value.toString());
        }
        if (physical_profile.target_waist) {
          setTargetWaist(physical_profile.target_waist.value.toString());
        }
      }
    }
  }, [user]);

  // T042: Validate form
  const validateForm = (): boolean => {
    const validationErrors: Record<string, string> = {};

    // Primary goal is required
    if (!primaryGoal) {
      validationErrors.primaryGoal = 'Please select your primary goal';
    }

    // Target weight must be positive if provided
    if (targetWeight) {
      const weightValue = parseFloat(targetWeight);
      if (isNaN(weightValue) || weightValue <= 0) {
        validationErrors.targetWeight = 'Target weight loss must be positive';
      }
    }

    // Target waist must be positive if provided
    if (targetWaist) {
      const waistValue = parseFloat(targetWaist);
      if (isNaN(waistValue) || waistValue <= 0) {
        validationErrors.targetWaist = 'Target waist reduction must be positive';
      }
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  // T043: Handle form submission
  const handleNext = async () => {
    if (!validateForm()) {
      return;
    }

    // Prepare data for API
    const pageData: any = {
      primary_goal: primaryGoal,
    };

    // Include optional targets in physical_profile if provided
    if (targetWeight || targetWaist) {
      pageData.physical_profile = {};

      if (targetWeight) {
        pageData.physical_profile.target_weight = {
          value: parseFloat(targetWeight),
          unit: 'kg'
        };
      }

      if (targetWaist) {
        pageData.physical_profile.target_waist = {
          value: parseFloat(targetWaist),
          unit: 'cm'
        };
      }
    }

    try {
      // Save goals via API
      await savePage.mutateAsync({ page: 'goals', data: pageData });

      // Success toast
      setToastType('success');
      setToastMessage('Goals saved successfully!');
      setShowToast(true);

      // T044: Navigate to next page
      setTimeout(() => {
        navigate('/onboarding/body-state');
      }, 1000);
    } catch (err: any) {
      // Error handling
      setToastType('error');
      setToastMessage(err.message || 'Failed to save goals. Please try again.');
      setShowToast(true);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-100 to-primary-200 px-4 py-12">
      <div className="max-w-2xl w-full animate-fade-in">
        {/* T037: Progress Indicator - Step 2 of 4, 50% */}
        <ProgressIndicator currentStep={2} totalSteps={4} />

        <div className="card shadow-elegant mt-8">
          {/* T036: Page header with emoticon */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-serif font-bold text-primary-800 mb-3">
              What do you want most right now? 🎯
            </h1>
            <p className="text-base text-neutral-600">
              Choose your primary goal to personalize your guidance
            </p>
          </div>

          {/* Toast notification with aria-live for screen readers (T086) */}
          {showToast && (
            <div
              role="alert"
              aria-live="assertive"
              aria-atomic="true"
              className={`mb-6 p-4 border-l-4 rounded-r-lg animate-slide-down ${
                toastType === 'success'
                  ? 'bg-green-50 border-green-400'
                  : 'bg-red-50 border-red-400'
              }`}
            >
              <p
                className={`text-sm font-medium ${
                  toastType === 'success' ? 'text-green-800' : 'text-red-800'
                }`}
              >
                {toastMessage}
              </p>
            </div>
          )}

          <div className="space-y-8">
            {/* T038-T039: Primary goal cards in 2x2 grid */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-4">
                Primary Goal
              </label>

              {/* 2x2 grid on desktop, vertical stack on mobile */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {PRIMARY_GOALS.map((goal) => (
                  <button
                    key={goal.value}
                    type="button"
                    onClick={() => setPrimaryGoal(goal.value)}
                    className={`p-6 rounded-lg border-2 text-center font-medium transition-all duration-200 ${
                      primaryGoal === goal.value
                        ? 'border-primary-600 bg-primary-50 shadow-md text-primary-800'
                        : 'border-primary-200 bg-white hover:border-primary-400 text-neutral-700'
                    }`}
                  >
                    {goal.label}
                  </button>
                ))}
              </div>

              {errors.primaryGoal && (
                <p className="text-sm text-red-600 mt-2">{errors.primaryGoal}</p>
              )}
            </div>

            {/* T040-T041: Optional secondary targets */}
            <div className="border-t border-neutral-200 pt-6">
              <h3 className="text-sm font-medium text-neutral-700 mb-4">
                Secondary Goal (Optional)
              </h3>
              <p className="text-sm text-neutral-600 mb-6">
                Set specific targets to help track your progress
              </p>

              <div className="space-y-4">
                {/* T040: Target weight loss */}
                <div>
                  <label
                    htmlFor="targetWeight"
                    className="block text-sm font-medium text-neutral-700 mb-2"
                  >
                    I want to lose <span className="text-neutral-500">(kg)</span>
                  </label>
                  <input
                    id="targetWeight"
                    type="number"
                    step="0.1"
                    value={targetWeight}
                    onChange={(e) => setTargetWeight(e.target.value)}
                    className={`input input-large ${errors.targetWeight ? 'border-red-400' : ''}`}
                    placeholder="e.g., 10"
                  />
                  {errors.targetWeight && (
                    <p className="text-sm text-red-600 mt-1">{errors.targetWeight}</p>
                  )}
                  {/* Warning for high targets (non-blocking) */}
                  {targetWeight && parseFloat(targetWeight) > 30 && (
                    <p className="text-sm text-yellow-700 mt-1">
                      That's a significant goal! Consider breaking it into smaller milestones.
                    </p>
                  )}
                </div>

                {/* T041: Target waist reduction */}
                <div>
                  <label
                    htmlFor="targetWaist"
                    className="block text-sm font-medium text-neutral-700 mb-2"
                  >
                    I want to reduce my waist by <span className="text-neutral-500">(cm)</span>
                  </label>
                  <input
                    id="targetWaist"
                    type="number"
                    step="0.1"
                    value={targetWaist}
                    onChange={(e) => setTargetWaist(e.target.value)}
                    className={`input input-large ${errors.targetWaist ? 'border-red-400' : ''}`}
                    placeholder="e.g., 5"
                  />
                  {errors.targetWaist && (
                    <p className="text-sm text-red-600 mt-1">{errors.targetWaist}</p>
                  )}
                  {/* Warning for high targets (non-blocking) */}
                  {targetWaist && parseFloat(targetWaist) > 30 && (
                    <p className="text-sm text-yellow-700 mt-1">
                      That's a significant goal! Consider breaking it into smaller milestones.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Submit button */}
            <button
              type="button"
              onClick={handleNext}
              disabled={isSaving}
              className="btn btn-primary w-full text-lg py-4 mt-8 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Next Step →'}
            </button>
          </div>
        </div>

        {/* Footer note */}
        <p className="mt-6 text-xs text-center text-neutral-600">
          You can update your goals anytime from your profile settings
        </p>
      </div>
    </div>
  );
};

export default GoalsPage;
